import React from "react";
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  TextField,
  Button,
  MenuItem,
  Snackbar,
  Drawer,
  Divider,
  Checkbox,
  FormControlLabel,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { Fullscreen, FullscreenExit, Close } from "@material-ui/icons";
import { JOINTS, AILMENTS, MARKETS, LOCALES, PREMIUM_TYPES, SUBSCRIPTION_PRODUCTS } from "../../api";
import { useMedicationSubstances, useMedicationBrands } from "../../queries";
import { PATIENT_VARIABLES } from "../../patientVariables";
import type { Joint, Ailment, Market, PreviewLocale, PremiumType, SubscriptionProduct, PreviewAttrs } from "../../api";
import {
  AutoFillContext,
  FormValuesContext,
  FeelContext,
  ValidationContext,
  RespondedContext,
  type FormValues,
  type FormEntry,
} from "./ResolvedComponents";
import { previewStyles as styles } from "./previewStyles";

const formatAttrsSummary = (attrs: PreviewAttrs) => {
  const joint = attrs.ailment === "joint_pain" ? `${attrs.joint.replace(/_/g, " ")} · ` : "";
  const base = `${joint}${attrs.ailment.replace(/_/g, " ")} · ${attrs.market.toUpperCase()} · ${attrs.locale}`;
  const extras = [
    attrs.premium !== "none" ? attrs.premium.replace(/_/g, " ") : "",
    attrs.medication.length > 0 ? `${attrs.medication.length} meds` : "",
    attrs.medicationBrand.length > 0 ? `${attrs.medicationBrand.length} brands` : "",
    attrs.dueMedicationBrand.length > 0 ? `${attrs.dueMedicationBrand.length} due` : "",
    attrs.subscription.length > 0 ? `${attrs.subscription.length} subs` : "",
    attrs.canMessageTherapist ? "" : "no therapist messaging",
  ].filter(Boolean);
  return [base, ...extras].join(" · ");
};

const arraysEqual = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

type CatalogRow = { key: string; label: string };

const withOrphanedKeys = <T extends CatalogRow>(rows: readonly T[], selected: readonly string[]) => {
  const known = new Set(rows.map(r => r.key));
  const orphans = selected.filter(key => !known.has(key)).map(key => ({ key, label: key }));
  return [...rows, ...orphans];
};

const matching = (rows: readonly CatalogRow[], filter: string, selected: readonly string[]) => {
  const needle = filter.trim().toLowerCase();
  if (!needle) return rows;
  return rows.filter(r => r.label.toLowerCase().includes(needle) || selected.includes(r.key));
};

const toggled = (selected: readonly string[], key: string, rows: readonly CatalogRow[]) => {
  const next = new Set(selected);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return rows.map(r => r.key).filter(k => next.has(k));
};

const hasResponse = (value: unknown): boolean =>
  value != null &&
  (typeof value === "string" ? value.trim().length > 0 : Array.isArray(value) ? value.length > 0 : true);

const attrsEqual = (a: PreviewAttrs, b: PreviewAttrs) =>
  a.ailment === b.ailment &&
  (a.ailment === "joint_pain" && b.ailment === "joint_pain" ? a.joint === b.joint : true) &&
  a.market === b.market &&
  a.locale === b.locale &&
  a.premium === b.premium &&
  arraysEqual(a.medication, b.medication) &&
  arraysEqual(a.medicationBrand, b.medicationBrand) &&
  arraysEqual(a.dueMedicationBrand, b.dueMedicationBrand) &&
  arraysEqual(a.subscription, b.subscription) &&
  a.canMessageTherapist === b.canMessageTherapist;

export const PatientPreviewShell: React.VFC<{
  isExpanded: boolean;
  onToggleExpand: () => void;
  isLoading: boolean;
  error: unknown;
  attrs: PreviewAttrs;
  onApplyAttrs: (attrs: PreviewAttrs) => void;
  seedFeelContext?: Record<string, unknown>;
  children: React.ReactNode;
}> = ({ isExpanded, onToggleExpand, isLoading, error, attrs, onApplyAttrs, seedFeelContext, children }) => {
  const [errorOpen, setErrorOpen] = React.useState(false);

  React.useEffect(() => {
    if (error) setErrorOpen(true);
  }, [error]);

  const errorMessage = error instanceof Error ? error.message : "Failed to load preview";

  const [attrsOpen, setAttrsOpen] = React.useState(false);
  const [draftAttrs, setDraftAttrs] = React.useState<PreviewAttrs>(attrs);
  const openAttrs = () => {
    setDraftAttrs(attrs);
    setAttrsOpen(true);
  };
  const closeAttrs = () => setAttrsOpen(false);
  const confirmAttrs = () => {
    onApplyAttrs(draftAttrs);
    setAttrsOpen(false);
  };
  const {
    data: medicationSubstances = [],
    isLoading: substancesLoading,
    isError: substancesFailed,
  } = useMedicationSubstances(attrsOpen);
  const [medicationFilter, setMedicationFilter] = React.useState("");
  const substanceRows = React.useMemo(
    () => withOrphanedKeys(medicationSubstances, draftAttrs.medication),
    [medicationSubstances, draftAttrs.medication]
  );
  const visibleSubstances = React.useMemo(
    () => matching(substanceRows, medicationFilter, draftAttrs.medication),
    [substanceRows, medicationFilter, draftAttrs.medication]
  );
  const toggleMedication = (key: string) =>
    setDraftAttrs(prev => ({ ...prev, medication: toggled(prev.medication, key, substanceRows) }));

  const {
    data: medicationBrands = [],
    isLoading: brandsLoading,
    isError: brandsFailed,
  } = useMedicationBrands(attrsOpen);
  const [brandFilter, setBrandFilter] = React.useState("");
  const brandRows = React.useMemo(
    () => withOrphanedKeys(medicationBrands, draftAttrs.medicationBrand),
    [medicationBrands, draftAttrs.medicationBrand]
  );
  const visibleBrands = React.useMemo(
    () => matching(brandRows, brandFilter, draftAttrs.medicationBrand),
    [brandRows, brandFilter, draftAttrs.medicationBrand]
  );
  const substanceKeyOfBrand = (key: string) => {
    const brand = medicationBrands.find(b => b.key === key);
    const substance = medicationSubstances.find(s => s.id === brand?.substanceId);
    return substance?.key;
  };
  const toggleMedicationBrand = (key: string) =>
    setDraftAttrs(prev => {
      const medicationBrand = toggled(prev.medicationBrand, key, brandRows);
      if (!medicationBrand.includes(key))
        return { ...prev, medicationBrand, dueMedicationBrand: prev.dueMedicationBrand.filter(k => k !== key) };
      const substance = substanceKeyOfBrand(key);
      if (!substance || prev.medication.includes(substance)) return { ...prev, medicationBrand };
      return { ...prev, medicationBrand, medication: toggled(prev.medication, substance, substanceRows) };
    });
  const dueBrandRows = brandRows.filter(b => draftAttrs.medicationBrand.includes(b.key));
  const toggleDueMedicationBrand = (key: string) =>
    setDraftAttrs(prev => ({ ...prev, dueMedicationBrand: toggled(prev.dueMedicationBrand, key, brandRows) }));
  const toggleSubscription = (p: SubscriptionProduct) =>
    setDraftAttrs(prev => {
      const selected = new Set(prev.subscription);
      if (selected.has(p)) selected.delete(p);
      else selected.add(p);
      return { ...prev, subscription: SUBSCRIPTION_PRODUCTS.filter(s => selected.has(s)) };
    });

  const [autoFillTrigger, setAutoFillTrigger] = React.useState(0);
  const [clearKey, setClearKey] = React.useState(0);
  const [formValues, setFormValues] = React.useState<FormValues>({});
  const formValuesRef = React.useRef<FormValues>(formValues);
  formValuesRef.current = formValues;
  const requiredIdsRef = React.useRef<Map<number, number | null>>(new Map());
  const [errorIds, setErrorIds] = React.useState<Set<number>>(new Set());

  const formValuesApi = React.useMemo(
    () => ({
      register: (questionId: number, entry: FormEntry) =>
        setFormValues(prev => (prev[questionId] === entry ? prev : { ...prev, [questionId]: entry })),
      unregister: (questionId: number) =>
        setFormValues(prev => {
          if (!(questionId in prev)) return prev;
          const next = { ...prev };
          delete next[questionId];
          return next;
        }),
    }),
    []
  );

  // The FEEL context and the answered-question set are pure derivations of the
  // current answers plus the schema baseline (`seedFeelContext`: every option
  // feel-id defined). Deriving instead of accumulating means visibility never
  // depends on mount/registration order, and a predicate like `q = optionX`
  // can't collapse into `null = null` just because `q` is unanswered.
  const feelContext = React.useMemo(() => {
    const ctx: Record<string, unknown> = { ...seedFeelContext };
    for (const entry of Object.values(formValues)) {
      if (entry.feelId) ctx[entry.feelId] = entry.feelValue;
    }
    return ctx;
  }, [seedFeelContext, formValues]);

  const respondedIds = React.useMemo(() => {
    const ids = new Set<number>();
    for (const entry of Object.values(formValues)) {
      if (hasResponse(entry.value)) ids.add(entry.questionId);
    }
    return ids;
  }, [formValues]);

  React.useEffect(() => {
    setErrorIds(prev => {
      if (prev.size === 0) return prev;
      const next = new Set([...prev].filter(id => !hasResponse(formValues[id]?.value)));
      return next.size === prev.size ? prev : next;
    });
  }, [formValues]);

  const validationApi = React.useMemo(
    () => ({
      registerRequired: (id: number, pageIndex: number | null) => {
        requiredIdsRef.current.set(id, pageIndex);
      },
      unregisterRequired: (id: number) => {
        requiredIdsRef.current.delete(id);
      },
    }),
    []
  );

  const validate = React.useCallback((pageIndex?: number | null): boolean => {
    const errors = new Set<number>();
    for (const [id, page] of requiredIdsRef.current) {
      if (pageIndex != null && page !== pageIndex) continue;
      if (!hasResponse(formValuesRef.current[id]?.value)) {
        errors.add(id);
      }
    }
    setErrorIds(errors);
    return errors.size === 0;
  }, []);

  const validationContextValue = React.useMemo(
    () => ({ ...validationApi, errorIds, validate }),
    [validationApi, errorIds, validate]
  );

  const handleSubmit = () => {
    if (!validate()) return;
    const payload = Object.values(formValuesRef.current).map(({ questionId, value }) => ({ questionId, value }));
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "health-form-values.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className={styles.previewContainer}>
      <Box display="flex" alignItems="center" px={1} pt={1} style={{ gap: 4 }}>
        <Button
          size="small"
          variant="outlined"
          onClick={openAttrs}
          style={{ fontSize: 11, height: 24, textTransform: "none", padding: "0 8px" }}
        >
          {formatAttrsSummary(attrs)}
        </Button>
        {isLoading && <CircularProgress size={14} />}
      </Box>
      <Drawer anchor="right" open={attrsOpen} onClose={closeAttrs}>
        <Box display="flex" flexDirection="column" style={{ width: 420, height: "100%" }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={1.5}>
            <Typography variant="h6">Preview attributes</Typography>
            <IconButton size="small" onClick={closeAttrs}>
              <Close />
            </IconButton>
          </Box>
          <Divider />
          <Box flex={1} px={2} py={2} display="flex" flexDirection="column" style={{ gap: 16, overflowY: "auto" }}>
            <Box display="grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <TextField
                select
                variant="outlined"
                label={PATIENT_VARIABLES.joint.pickerLabel}
                disabled={draftAttrs.ailment !== "joint_pain"}
                value={draftAttrs.ailment === "joint_pain" ? draftAttrs.joint : ""}
                onChange={e =>
                  setDraftAttrs(prev =>
                    prev.ailment === "joint_pain" ? { ...prev, joint: e.target.value as Joint } : prev
                  )
                }
                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
              >
                {JOINTS.map(j => (
                  <MenuItem key={j} value={j}>
                    {j.replace(/_/g, " ")}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                variant="outlined"
                label={PATIENT_VARIABLES.ailment.pickerLabel}
                value={draftAttrs.ailment}
                onChange={e => {
                  const ailment = e.target.value as Ailment;
                  setDraftAttrs(prev =>
                    ailment === "joint_pain"
                      ? { ...prev, ailment, joint: prev.ailment === "joint_pain" ? prev.joint : "knee" }
                      : {
                          ailment,
                          market: prev.market,
                          locale: prev.locale,
                          premium: prev.premium,
                          medication: prev.medication,
                          medicationBrand: prev.medicationBrand,
                          dueMedicationBrand: prev.dueMedicationBrand,
                          subscription: prev.subscription,
                          canMessageTherapist: prev.canMessageTherapist,
                        }
                  );
                }}
              >
                {AILMENTS.map(a => (
                  <MenuItem key={a} value={a}>
                    {a.replace(/_/g, " ")}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                variant="outlined"
                label={PATIENT_VARIABLES.market.pickerLabel}
                value={draftAttrs.market}
                onChange={e => setDraftAttrs({ ...draftAttrs, market: e.target.value as Market })}
              >
                {MARKETS.map(m => (
                  <MenuItem key={m} value={m}>
                    {m.toUpperCase()}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                variant="outlined"
                label={PATIENT_VARIABLES.locale.pickerLabel}
                value={draftAttrs.locale}
                onChange={e => setDraftAttrs({ ...draftAttrs, locale: e.target.value as PreviewLocale })}
              >
                {LOCALES.map(l => (
                  <MenuItem key={l} value={l}>
                    {l}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                variant="outlined"
                label={PATIENT_VARIABLES.premium.pickerLabel}
                value={draftAttrs.premium}
                onChange={e => setDraftAttrs({ ...draftAttrs, premium: e.target.value as PremiumType })}
              >
                {PREMIUM_TYPES.map(p => (
                  <MenuItem key={p} value={p}>
                    {p.replace(/_/g, " ")}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    color="primary"
                    checked={draftAttrs.canMessageTherapist}
                    onChange={e => setDraftAttrs({ ...draftAttrs, canMessageTherapist: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">{PATIENT_VARIABLES.canMessageTherapist.pickerLabel}</Typography>}
              />
            </Box>
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography variant="subtitle2" color="textSecondary">
                  {PATIENT_VARIABLES.subscription.pickerLabel}
                  {draftAttrs.subscription.length > 0 ? ` (${draftAttrs.subscription.length})` : ""}
                </Typography>
                {draftAttrs.subscription.length > 0 && (
                  <Button size="small" onClick={() => setDraftAttrs({ ...draftAttrs, subscription: [] })}>
                    Clear
                  </Button>
                )}
              </Box>
              <Box display="grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
                {SUBSCRIPTION_PRODUCTS.map(p => (
                  <FormControlLabel
                    key={p}
                    control={
                      <Checkbox
                        size="small"
                        color="primary"
                        checked={draftAttrs.subscription.includes(p)}
                        onChange={() => toggleSubscription(p)}
                      />
                    }
                    label={<Typography variant="body2">{p.replace(/_/g, " ")}</Typography>}
                  />
                ))}
              </Box>
            </Box>
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography variant="subtitle2" color="textSecondary">
                  {PATIENT_VARIABLES.medication.pickerLabel}
                  {draftAttrs.medication.length > 0 ? ` (${draftAttrs.medication.length})` : ""}
                </Typography>
                {draftAttrs.medication.length > 0 && (
                  <Button size="small" onClick={() => setDraftAttrs({ ...draftAttrs, medication: [] })}>
                    Clear
                  </Button>
                )}
              </Box>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Search substances"
                value={medicationFilter}
                onChange={e => setMedicationFilter(e.target.value)}
              />
              {substancesLoading ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={20} />
                </Box>
              ) : substancesFailed ? (
                <Alert severity="error">Failed to load the substance catalog</Alert>
              ) : (
                <Box display="grid" mt={0.5} maxHeight={260} overflow="auto" style={{ gridTemplateColumns: "1fr 1fr" }}>
                  {visibleSubstances.map(m => (
                    <FormControlLabel
                      key={m.key}
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={draftAttrs.medication.includes(m.key)}
                          onChange={() => toggleMedication(m.key)}
                        />
                      }
                      label={<Typography variant="body2">{m.label}</Typography>}
                    />
                  ))}
                  {visibleSubstances.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                      No substances match
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            <Box>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                <Typography variant="subtitle2" color="textSecondary">
                  {PATIENT_VARIABLES.medicationBrand.pickerLabel}
                  {draftAttrs.medicationBrand.length > 0 ? ` (${draftAttrs.medicationBrand.length})` : ""}
                </Typography>
                {draftAttrs.medicationBrand.length > 0 && (
                  <Button
                    size="small"
                    onClick={() => setDraftAttrs({ ...draftAttrs, medicationBrand: [], dueMedicationBrand: [] })}
                  >
                    Clear
                  </Button>
                )}
              </Box>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                placeholder="Search brands"
                value={brandFilter}
                onChange={e => setBrandFilter(e.target.value)}
              />
              {brandsLoading ? (
                <Box display="flex" justifyContent="center" py={2}>
                  <CircularProgress size={20} />
                </Box>
              ) : brandsFailed ? (
                <Alert severity="error">Failed to load the brand catalog</Alert>
              ) : (
                <Box display="grid" mt={0.5} maxHeight={260} overflow="auto" style={{ gridTemplateColumns: "1fr 1fr" }}>
                  {visibleBrands.map(b => (
                    <FormControlLabel
                      key={b.key}
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={draftAttrs.medicationBrand.includes(b.key)}
                          onChange={() => toggleMedicationBrand(b.key)}
                        />
                      }
                      label={<Typography variant="body2">{b.label}</Typography>}
                    />
                  ))}
                  {visibleBrands.length === 0 && (
                    <Typography variant="body2" color="textSecondary">
                      No brands match
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                {PATIENT_VARIABLES.dueMedicationBrand.pickerLabel}
                {draftAttrs.dueMedicationBrand.length > 0 ? ` (${draftAttrs.dueMedicationBrand.length})` : ""}
              </Typography>
              {dueBrandRows.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  Pick brands above to mark them due
                </Typography>
              ) : (
                <Box display="grid" mt={0.5} style={{ gridTemplateColumns: "1fr 1fr" }}>
                  {dueBrandRows.map(b => (
                    <FormControlLabel
                      key={b.key}
                      control={
                        <Checkbox
                          size="small"
                          color="primary"
                          checked={draftAttrs.dueMedicationBrand.includes(b.key)}
                          onChange={() => toggleDueMedicationBrand(b.key)}
                        />
                      }
                      label={<Typography variant="body2">{b.label}</Typography>}
                    />
                  ))}
                </Box>
              )}
            </Box>
          </Box>
          <Divider />
          <Box display="flex" justifyContent="flex-end" px={2} py={1.5} style={{ gap: 8 }}>
            <Button onClick={closeAttrs}>Cancel</Button>
            <Button variant="contained" color="primary" onClick={confirmAttrs} disabled={attrsEqual(draftAttrs, attrs)}>
              Apply
            </Button>
          </Box>
        </Box>
      </Drawer>
      <Snackbar
        open={errorOpen}
        autoHideDuration={4000}
        onClose={() => setErrorOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorOpen(false)}>
          {errorMessage}
        </Alert>
      </Snackbar>
      <Box display="flex" alignItems="center" px={1} pt={0.5} style={{ gap: 8 }}>
        <Button
          size="small"
          onClick={() => setAutoFillTrigger(n => n + 1)}
          style={{ fontSize: 11, minWidth: 0, padding: "2px 8px" }}
        >
          Autofill
        </Button>
        <Button
          size="small"
          onClick={() => {
            setClearKey(n => n + 1);
            setAutoFillTrigger(0);
            setFormValues({});
          }}
          style={{ fontSize: 11, minWidth: 0, padding: "2px 8px" }}
        >
          Clear
        </Button>
        <Button size="small" onClick={handleSubmit} style={{ fontSize: 11, minWidth: 0, padding: "2px 8px" }}>
          Submit
        </Button>
        <Box style={{ flex: 1 }} />
        <Tooltip title={isExpanded ? "Minimize" : "Expand"}>
          <IconButton size="small" onClick={onToggleExpand}>
            {isExpanded ? <FullscreenExit /> : <Fullscreen />}
          </IconButton>
        </Tooltip>
      </Box>

      {error && !isLoading && (
        <Box px={2} py={4} textAlign="center">
          <Typography color="error">{errorMessage}</Typography>
        </Box>
      )}
      <RespondedContext.Provider value={respondedIds}>
        <FormValuesContext.Provider value={formValuesApi}>
          <FeelContext.Provider value={feelContext}>
            <ValidationContext.Provider value={validationContextValue}>
              <AutoFillContext.Provider value={autoFillTrigger} key={clearKey}>
                {children}
              </AutoFillContext.Provider>
            </ValidationContext.Provider>
          </FeelContext.Provider>
        </FormValuesContext.Provider>
      </RespondedContext.Provider>
    </div>
  );
};
